// Angular dependencies
import {
    Component,
    Input,
    HostBinding,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy
} from '@angular/core';

import {ENTER} from '@angular/cdk/keycodes';

import { ColorEvent } from 'ngx-color';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { tap, map, skip, filter, switchMap, startWith, combineLatest, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
    DECORATION_CONVERTATION_COST,
    DECORATION_PIXELIZATION_COST
} from '../constants';
import { ApiService } from '../../core/upgraded-providers';
import { WorldConfigService } from '../world-config.service';
import { creepPreviewRoom, CREEP_PREVIEW_STATE } from '../../../../stories/mocks/rooms/creep-preview';

const VIEW_ACTIVATION = 'activation';
const VIEW_PREVIEW_ITEM = 'preview';
const VIEW_PREVIEW_LIST = 'preview-list';
const VIEW_DEACTIVATION = 'deactivation';
const VIEW_PIXELIZATION = 'pixelization';

const INHERIT_PROPS = ['shard', 'room'];
const ANIMATIONS = [{
    value: '', label: 'None'
}, {
    value: 'slow', label: 'Slow'
}, {
    value: 'fast', label: 'Fast'
}, {
    value: 'blink', label: 'Blink'
}, {
    value: 'neon', label: 'Neon'
}, {
    value: 'flash', label: 'Flash'
}];

@Component({
    selector: 'app-decoration',
    templateUrl: './decoration.component.pug',
    styleUrls: ['./decoration.component.less']
})
export class DecorationComponent implements OnInit, OnDestroy {
    _destroySbj: Subject<void> = new Subject();

    separatorKeysCodes: number[] = [ENTER];

    creepPreviewRoom = creepPreviewRoom;
    _creepPreviewStateSbj: BehaviorSubject<IGameState> = new BehaviorSubject({});
    creepPreviewState$: Observable<IGameState> = this._creepPreviewStateSbj.asObservable();

    ANIMATIONS = ANIMATIONS;
    VIEW_ACTIVATION = VIEW_ACTIVATION;
    VIEW_PREVIEW_ITEM = VIEW_PREVIEW_ITEM;
    VIEW_PREVIEW_LIST = VIEW_PREVIEW_LIST;
    VIEW_DEACTIVATION = VIEW_DEACTIVATION;
    VIEW_PIXELIZATION = VIEW_PIXELIZATION;

    @Input() userDecorationId: string = null;
    @Input() user: any = null;
    @Input() pixels: number;
    @Input() themes: any[] = [];

    _roomStateSbj: BehaviorSubject<IGameState> = new BehaviorSubject(null);
    roomState$: Observable<IGameState> = this._roomStateSbj.asObservable();

    _activeSbj: BehaviorSubject<any> = new BehaviorSubject({});
    active$: Observable<any> = this._activeSbj.asObservable();

    _decorationsSbj: BehaviorSubject<any[]> = new BehaviorSubject([]);
    decorationsActivated$: Observable<any[]> = this._decorationsSbj.asObservable()
        .pipe(map((decorations) => decorations.filter(({ active: _ }) => !!_)));

    creepActivatedDecorations$: Observable<any[]> = this._decorationsSbj.asObservable()
        .pipe(switchMap(() => this.active$))
        .pipe(map(() => {
            const decorations = this._decorationsSbj.getValue().filter(({ active: _, decoration: { type } }) => {
                return !!_ && type === 'creep';
            }).map(({ active, decoration }) => ({
                ...active,
                user: this.user._id,
                decoration
            }));

            decorations.push({
                ...this.active,
                user: this.user._id,
                decoration: this.decoration,
            });

            return decorations;
        }));

    decorations$: Observable<any[]> = this._decorationsSbj.asObservable()
        .pipe(switchMap(() => this.active$))
        .pipe(filter(({ shard, room }) => shard && room))
        .pipe(map(({ shard, room }) => {
            return this._decorationsSbj.getValue().filter(({ active, decoration: { type } }) => {
                if (type === 'creep' && active) {
                    return true;
                }

                return active && active.shard === shard && active.room === room;
            });
        }))
        .pipe(map((decorations) => decorations.map(({ active, decoration, user }) => ({ ...active, decoration, user }))));

    _roomsSbj: BehaviorSubject<any> = new BehaviorSubject(null);
    rooms$: Observable<any> = this._roomsSbj.asObservable()
        .pipe(combineLatest(this.decorationsActivated$))
        .pipe(map(([rooms, userDecorations]) => {
            let _userDecorations = [];
            if (['wallLandscape', 'floorLandscape', 'landscape'].includes(this.decoration.type)) {
                _userDecorations = userDecorations.filter((userDecoration) => {
                    const { decoration } = userDecoration;

                    if (!['wallLandscape', 'floorLandscape', 'landscape'].includes(decoration.type)) {
                        return;
                    }

                    if (this.decoration.type === 'wallLandscape' && decoration.type === 'floorLandscape') {
                        return;
                    }

                    if (this.decoration.type === 'floorLandscape' && decoration.type === 'wallLandscape') {
                        return;
                    }

                    return true;
                });
            }

            if (['metadata', 'object'].includes(this.decoration.type)) {
                _userDecorations = userDecorations.filter((userDecoration) => {
                    const { decoration } = userDecoration;

                    if (this.decoration.type !== decoration.type) {
                        return false;
                    }

                    return true;
                });
            }

            _userDecorations.forEach((userDecoration) => {
                const { active } = userDecoration;

                if (!active || !active.shard || !active.room || !rooms || !rooms[active.shard]) {
                    return;
                }

                const idx = rooms[active.shard].findIndex(({ room }) => room === active.room);
                if (idx === -1) {
                    return;
                }

                rooms[active.shard] = [...rooms[active.shard]];
                rooms[active.shard].splice(idx, 1, { ...rooms[active.shard][idx], disabled: true, userDecoration });
            });

            return rooms;
        }));

    @Input() set rooms(rooms: any[]) {
        if (!rooms) {
            return;
        }

        this._roomsSbj.next({
            ...rooms
        });
    }

    @Input() set userDecorations(decorations: any[]) {
        this._decorationsSbj.next(decorations);
    }

    _active: any = { };
    @Input() set active(value) {
        console.debug('set DecorationComponent::active', value);
        this._active = value || { };
    }
    get active() {
        // console.log('get DecorationComponent::active');
        return this._active;
    }

    _decorationSbj: BehaviorSubject<any> = new BehaviorSubject(null);
    public decoration$: Observable<any> = this._decorationSbj.asObservable();
    // _decoration: any;
    @Input() set decoration(decoration) {
        console.debug('set DecorationComponent::decoration', decoration);

        this._decorationSbj.next(decoration);
        if (!decoration) {
            return;
        }

        const active = {};
        try {
            Object.keys(this.active).forEach((name) => {
                if (!decoration.props.hasOwnProperty(name) && !INHERIT_PROPS.includes(name)) {
                    return;
                }

                active[name] = this.active[name];
            });
        } catch (err) {
            // Noop.
        }

        try {
            this.props.forEach(([name, s]) => {
                if (active.hasOwnProperty(name)) {
                    return;
                }

                active[name] = s.default;
            });
        } catch (err) {
            // Noop.
        }

        // if (decoration.type === 'object') {
        //     active['user'] = this.user._id;
        // }

        this.active = active;
    }
    get decoration() {
        return this._decorationSbj.getValue();
    }

    public decorations;

    @Output() close: EventEmitter<any> = new EventEmitter();
    @Output() pixelize: EventEmitter<any> = new EventEmitter();
    @Output() convert: EventEmitter<any> = new EventEmitter();
    @Output() activate: EventEmitter<any> = new EventEmitter();
    @Output() deactivate: EventEmitter<any> = new EventEmitter();

    @Input() view: string;
    @Input() showDescriptionContent: boolean = true;
    @Input() modal: boolean = false;

    @HostBinding('class') get classes(): string {
        const classes = [];
        if (this.modal) {
            classes.push('--modal');
        }

        if (this.view) {
            classes.push(`--${ this.view }`);
        }

        if (this.decoration && this.decoration.rarity) {
            classes.push(`--rarity-${ this.decoration.rarity }`);
        }

        return classes.join(' ');
    }

    @HostBinding('attr.disabled') disabled;

    get props(): [string, any][] {
        try {
            return Object.entries(this.decoration.props).filter(([, _]) => _);
        } catch (err) {
            return [];
        }
    }

    _colorPropsSbj: BehaviorSubject<any[]> = new BehaviorSubject([]);
    colroProps$: Observable<any[]> = this._colorPropsSbj.asObservable();
    get colorProps() {
        return getDecorationColorProps(this.decoration);
    }

    get displayProps() {
        return this.props.filter(([, { type, readonly }]) => type === 'display' && !readonly);
    }

    get booleanProps() {
        return this.props.filter(([, { type, readonly }]) => type === 'boolean' && !readonly);
    }

    get rangeProps() {
        const colorProps = this.colorProps;

        return this.props.filter(([prop, { type, readonly, label }]) => {
            if (readonly || type !== 'range') {
                return false;
            }

            if (/Alpha$/.test(prop)) {
                const propColor = prop.substr(0, prop.length - 5) + 'Color';

                if (this.decoration.props.hasOwnProperty(propColor)) {
                    if (['creep', 'wallGraffiti'].includes(this.decoration.type)) {
                        return false;
                    }
                }
            }

            if (prop === 'alpha') {
                if (colorProps.some(([prop]) => 'mainColor' === prop || 'color' === prop)) {
                    return false;
                }
            }

            return true;
        });
    }

    get animationProps() {
        return this.props.filter(([, { label, readonly }]) => label === 'Animation' && !readonly);
    }

    get inputProps() {
        return this.props.filter(([, { type, label, readonly }]) => type === 'string' && label !== 'Animation' && !readonly);
    }

    DECORATION_CONVERTATION_COST: number = DECORATION_CONVERTATION_COST;
    DECORATION_PIXELIZATION_COST: number = DECORATION_PIXELIZATION_COST;
    constructor(
        private _apiService: ApiService,
        public worldConfig: WorldConfigService
    ) {
    }

    ngOnInit() {
        this.decoration$
            .pipe(takeUntil(this._destroySbj))
            .pipe(filter((decoration) => decoration))
            .pipe(filter(({ type }) => type === 'creep'))
            .pipe(tap(() => {
                this._creepPreviewStateSbj.next({
                    objects: [
                        ...CREEP_PREVIEW_STATE.objects.map((data) => ({
                            ...data,
                            name: this.active.exclude ? '' : this.active.nameFilter,
                            user: this.user._id
                        }))
                    ],
                    users: {
                        [this.user._id]: {
                            _id: this.user._id,
                            username: this.user.username,
                            badge: this.user.badge
                        }
                    }
                });
            }))
            .pipe(switchMap(() => this.active$))
            .pipe(skip(1))
            .pipe(tap((active) => {
                const { users, objects } = this._creepPreviewStateSbj.getValue();

                this._creepPreviewStateSbj.next({
                    objects: [
                        ...objects.map((o) => {
                            if (o.type === 'creep') {
                                o._id = Math.random().toString();
                                o.name = '';
                                if (!active.exclude) {
                                    o.name = active.nameFilter;
                                }
                            }

                            return { ...o };
                        })
                    ],
                    users
                });
            }))
            .subscribe();

        this.active$
            .pipe(takeUntil(this._destroySbj))
            .pipe(filter(({ shard, room }) => shard && room))
            .pipe(distinctUntilChanged((a, b) => a.room === b.room && a.shard === b.shard))
            .pipe(switchMap(({ shard, room }) => this._getRoomObjects(shard, room)))
            .pipe(tap((state) => this._roomStateSbj.next(state)))
            .subscribe();
    }

    ngOnDestroy() {
        this._destroySbj.next();
        this._destroySbj.complete();
    }

    onPixelization() {
        this.modal = true;
    }

    onPixelize(params) {
        this.view = VIEW_PIXELIZATION;
        const cancel = () => this.view = '';

        const preview = (decorations) => {
            if (decorations.length === 1) {
                this.view = VIEW_PREVIEW_ITEM;
                this.decoration = decorations[0].decoration;
            } else {
                this.view = VIEW_PREVIEW_LIST;
                this.decorations = decorations;
            }
        };

        this.pixelize.emit({ params, cancel, preview });
    }

    onConvert() {
        this.disabled = true;
        const cancel = () => this.disabled = false;
        const close = () => this.onClose();

        this.convert.emit({ cancel, close });
    }

    onActivate(active) {
        this.disabled = true;
        const cancel = () => this.disabled = false;
        const success = () => this.view = VIEW_DEACTIVATION;
        const close = () => this.onClose();

        this.activate.emit({ active, cancel, success, close });
    }

    onDeactivate() {
        this.disabled = true;
        const cancel = () => this.disabled = false;
        const success = () => {
            this.view = VIEW_ACTIVATION;
            this.disabled = false;
        };

        this.deactivate.emit({ cancel, success });
    }

    onStartUse() {
        this.view = VIEW_ACTIVATION;
    }

    async onClose() {
        this.close.emit();

        await delay(0);

        if (this.view === VIEW_PIXELIZATION) {
            return;
        }

        if (this.modal) {
            this.active = { };
        }

        this.view = '';
        this.modal = false;
        this.decoration = null;
        this.decorations = null;
        this.disabled = false;
    }

    listProp(prop) {
        if (this.active[prop]) {
            return this.active[prop].split('!SEP!');
        }

        return [];
    }

    onAddItemListProp(prop, value) {
        if (!value) {
            return;
        }

        const _value = (this.active[prop] ? `${ this.active[prop] }!SEP!` : '') + value.trim();

        this.active = {
            ...this.active,
            [prop]: _value
        };

        this._activeSbj.next(this.active);
    }

    onRemoveItemListProp(prop, value) {
        const _value = this.active[prop].split('!SEP!').filter((_) => _ !== value).join('!SEP!');

        this.active = {
            ...this.active,
            [prop]: _value
        };

        this._activeSbj.next(this.active);
    }

    onChangeActiveProp(prop, value) {
        this.active = {
            ...this.active,
            [prop]: value
        };

        this._activeSbj.next(this.active);
    }

    onChangeColorProp(prop, { color }: ColorEvent) {
        const hex = color.hex;
        const {a: alpha} = color.rgb;

        const _color: { [key: string]: any } = {
            [prop]: hex
        };

        let propAlpha;
        if (prop === 'mainColor' || prop === 'color') {
            propAlpha = 'alpha';
        } else if (/Color$/.test(prop)) {
            const _propAlpha = prop.substr(0, prop.length - 5) + 'Alpha';
            if (this.active.hasOwnProperty(_propAlpha)) {
                propAlpha = _propAlpha;
            }
        }

        if (propAlpha && this.decoration.props.hasOwnProperty(propAlpha)) {
            _color[propAlpha] = alpha;
        }

        this.active = {
            ...this.active,
            ..._color
        };

        this._activeSbj.next(this.active);
    }

    async _getRoomObjects(shard: string, room: string) {
        const { objects, users } = await this._apiService.get('game/room-objects', { room, shard });

        console.debug('DecorationComponent::getRoomObjects', {
            objects, users
        });

        return { objects, users };
    }

    trackByFn(index, item) {
        return index; // or item.id
    }
}

function delay(delayTime) {
    return new Promise((resolve) => setTimeout(resolve, delayTime));
}

function pad(num, base = 10): string {
    return num < base ? `0${num}` : num;
}

function activeProps(decoration) {
    const props = Object.entries(decoration.props);

    const active: { [key: string]: any } = {
    };

    props.forEach(([name, s]: [string, any]) => {
        active[name] = s.default;
    });

    return active;
}

function getDecorationColorProps(decoration: IDecoration) {
    try {
        const props = Object.entries(decoration.props);

        return props.filter(([prop, { type, readonly }]) => {
            if (readonly || type !== 'color') {
                return false;
            }

            if (!['creep', 'wallGraffiti'].includes(decoration.type)) {
                return true;
            }

            for (const graphic of decoration.graphics) {
                if (graphic.color === prop) {
                    return true;
                }
            }

            return false;
        });
    } catch (Err) {
    }

    return [];
}
